// ============================================================================
// BP-09-04: Forum Types
// BrightPath Community - Forums Feature
// ============================================================================

import { z } from 'zod';

// =============================================================================
// Enums
// =============================================================================

export const ForumPostTypeEnum = z.enum([
  'discussion',
  'question',
  'announcement',
  'poll',
]);
export type ForumPostType = z.infer<typeof ForumPostTypeEnum>;

export const ForumPostStatusEnum = z.enum([
  'active',
  'locked',
  'hidden',
  'deleted',
]);
export type ForumPostStatus = z.infer<typeof ForumPostStatusEnum>;

export const ForumReplyStatusEnum = z.enum([
  'active',
  'hidden',
  'deleted',
]);
export type ForumReplyStatus = z.infer<typeof ForumReplyStatusEnum>;

// =============================================================================
// Forum Category
// =============================================================================

export const ForumCategorySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  display_order: z.number(),
  diagnosis_specific: z.boolean(),
  diagnosis_id: z.string().uuid().nullable(),
  diagnosis_name: z.string().nullable(),
  requires_approval: z.boolean(),
  post_count: z.number(),
  last_post_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type ForumCategory = z.infer<typeof ForumCategorySchema>;

// =============================================================================
// Forum Post
// =============================================================================

export const ForumPostAuthorSchema = z.object({
  id: z.string().uuid(),
  first_name: z.string(),
  last_name: z.string(),
  avatar_url: z.string().nullable(),
  role: z.string(),
});

export type ForumPostAuthor = z.infer<typeof ForumPostAuthorSchema>;

export const ForumPostSchema = z.object({
  id: z.string().uuid(),
  category_id: z.string().uuid(),
  category_slug: z.string(),
  category_name: z.string(),
  author: ForumPostAuthorSchema,
  title: z.string(),
  content: z.string(),
  post_type: ForumPostTypeEnum,
  status: ForumPostStatusEnum,
  is_pinned: z.boolean(),
  is_locked: z.boolean(),
  view_count: z.number(),
  reply_count: z.number(),
  like_count: z.number(),
  last_reply_at: z.string().datetime().nullable(),
  last_reply_by: ForumPostAuthorSchema.nullable(),
  tags: z.array(z.string()),
  is_liked_by_user: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type ForumPost = z.infer<typeof ForumPostSchema>;

export const ForumPostListItemSchema = ForumPostSchema.omit({ content: true }).extend({
  excerpt: z.string(),
});

export type ForumPostListItem = z.infer<typeof ForumPostListItemSchema>;

// =============================================================================
// Forum Reply
// =============================================================================

export const ForumReplySchema = z.object({
  id: z.string().uuid(),
  post_id: z.string().uuid(),
  author: ForumPostAuthorSchema,
  parent_reply_id: z.string().uuid().nullable(),
  content: z.string(),
  like_count: z.number(),
  status: ForumReplyStatusEnum,
  is_solution: z.boolean(),
  is_liked_by_user: z.boolean(),
  replies: z.array(z.lazy((): z.ZodType => ForumReplySchema)).optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type ForumReply = z.infer<typeof ForumReplySchema>;

// =============================================================================
// Create/Update Schemas
// =============================================================================

export const CreateForumPostSchema = z.object({
  title: z.string().min(5, 'Title must be at least 5 characters').max(300),
  content: z.string().min(10, 'Content must be at least 10 characters'),
  post_type: ForumPostTypeEnum,
  tags: z.array(z.string()).max(5).optional(),
});

export type CreateForumPostInput = z.infer<typeof CreateForumPostSchema>;

export const UpdateForumPostSchema = CreateForumPostSchema.partial();

export type UpdateForumPostInput = z.infer<typeof UpdateForumPostSchema>;

export const CreateForumReplySchema = z.object({
  content: z.string().min(1, 'Reply cannot be empty'),
  parent_reply_id: z.string().uuid().optional(),
});

export type CreateForumReplyInput = z.infer<typeof CreateForumReplySchema>;

// =============================================================================
// Query Parameters
// =============================================================================

export interface ForumPostsQueryParams {
  category_slug?: string;
  post_type?: ForumPostType;
  search?: string;
  sort_by?: 'recent' | 'popular' | 'most_replies';
  page?: number;
  limit?: number;
}

export interface ForumSearchParams {
  query: string;
  category_id?: string;
  post_type?: ForumPostType;
  page?: number;
  limit?: number;
}

// =============================================================================
// Response Types
// =============================================================================

export interface ForumCategoriesResponse {
  categories: ForumCategory[];
}

export interface ForumPostsResponse {
  posts: ForumPostListItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface ForumPostDetailResponse {
  post: ForumPost;
  replies: ForumReply[];
}

export interface ForumRecentActivityResponse {
  recent_posts: ForumPostListItem[];
  popular_posts: ForumPostListItem[];
}
